import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { ButtonGroup, Button, ButtonToolbar, DropdownButton, Dropdown, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { setShowHelp } from '../actions/AppActions';
import TerminalService from '../services/TerminalService';
import FavoriteIcon from './favorites/FavoriteIcon';
import { modal } from './modal/ModalEmitter';
import DocsContainer from './docs/DocsContainer';
import { whichMenuItem, menuDefs } from '../util/page';
import MenuCrumb from './MenuCrumb';
import ReportTraining from './report/ReportTraining';
import '../styles/header.scss';
import PageAttachments from './page/PageAttachments';
import AuthService from '../services/AuthService';
import history from '../history';
import '@dsisolutions/git-notes-client';

const { GitNotes } = window;

GitNotes.init({
  projectId: 37191632,
  branch: 'main',
  hostname: 'https://dsi-notes-scu7u6fsqa-uc.a.run.app',
  showOpenButton: false,
});

const Header = ({
  action = '',
  children,
  hasDocs = false,
  itemId = '',
  pageName,
  showFavorite = true,
  showHeader = true,
  showInfo = true,
  showTerminal = false,
  title = '',
  related = [],
}) => {
  const dispatch = useDispatch();
  const { showHelp, branch, searchData, user } = useSelector(state => state.app);
  const page = useSelector(state => state.page);

  const isReport = () => {
    return _.get(page, ['data', 'page', 'RouteType']) === 'viewReport';
  };

  const getPageAttachments = () => {
    if (!page.attachments || !page.attachments.length) {
      return [];
    }

    // we only count attachments that have no branch specified or branch is included in attachment
    return page.attachments.filter(attachment => {
      if (!attachment.branchIds || !attachment.branchIds.length) {
        return true;
      }

      return attachment.branchIds.includes(branch.id);
    });
  };

  const hasAttachments = () => !!getPageAttachments().length;

  const toggleHelp = () => {
    dispatch(setShowHelp(!showHelp));
    localStorage.setItem('showHelp', !showHelp);
  };

  const generateTitle = () => {
    return `Blaze - ${title}`;
  };

  const openDocsModal = () => {
    modal.info({
      hidePrint: false,
      title: `${pageName} Documentation`,
      message: <DocsContainer pageName={pageName} raw />,
      headerLink: `/docs/${pageName}`,
      style: null,
    });
  };

  const openPageAttachments = () => {
    modal.info({
      hidePrint: true,
      title: `${pageName} Attachments`,
      message: <PageAttachments pageAttachments={getPageAttachments()} />,
      style: null,
    });
  };

  const renderReportTrainingButton = () => {
    return (
      <Button
        variant="secondary"
        title="Report Training"
        onClick={() => {
          modal.info({
            hidePrint: true,
            title: 'Report Training',
            message: <ReportTraining />,
            style: 'fitted',
          });
        }}
      >
        <i className="fad fa-graduation-cap" />
      </Button>
    );
  };

  const renderInfoButton = () => {
    return (
      <Button
        variant="secondary"
        title="Toggle Field Info"
        onClick={toggleHelp}
        active={showHelp}
        className="icon-only"
      >
        <i className="fas fa-info-circle fa-fw" />
      </Button>
    );
  };

  const renderTerminalButton = () => {
    return (
      <Button
        variant="dark"
        title="Launch Terminal"
        onClick={() => {
          TerminalService.open(pageName);
        }}
      >
        <i className="fas fa-external-link-alt fa-fw" /> Terminal
      </Button>
    );
  };

  const renderDocsButton = () => {
    return (
      <Button
        variant="secondary"
        title="Documentation"
        target="dsi-documentation"
        className="icon-only"
        onClick={openDocsModal}
      >
        <i className="fas fa-question-circle fa-fw" />
      </Button>
    );
  };

  const renderNotesButton = () => {
    if (user.username !== 'dsisupport') {
      return;
    }

    GitNotes.filePath = `/pages/${pageName}`;

    return (
      <Button variant="secondary" title="Notes" className="icon-only" onClick={() => GitNotes.toggleNote()}>
        <i className="fas fa-sticky-note" />
      </Button>
    );
  };

  const renderFavoriteIcon = () => {
    if (!showFavorite) {
      return null;
    }

    return <FavoriteIcon pageName={pageName} />;
  };

  const renderAttachments = () => {
    if (!hasAttachments()) {
      return;
    }

    return (
      <Button variant="secondary" className="icon-only" title="Attachments" onClick={openPageAttachments}>
        {hasAttachments() && <div className="count">{getPageAttachments().length}</div>}
        <i className="fad fa-paperclip fa-fw" />
      </Button>
    );
  };

  const renderRelated = () => {
    if (!related.length) {
      return null;
    }

    return (
      <div className="d-inline-block mr-1">
        <DropdownButton
          alignRight
          variant="secondary"
          title={<i className="fas fa-link fa-fw" />}
          // className="no-caret"
        >
          {related.map(name => {
            const meta = searchData[name] || {};
            return (
              <Dropdown.Item
                className="pl-2"
                key={name}
                onClick={() => history.push(`/${name}`)}
                disabled={!AuthService.hasPermission(name)}
              >
                <Badge>{name}</Badge>
                {meta.title}
              </Dropdown.Item>
            );
          })}
        </DropdownButton>
      </div>
    );
  };

  const renderToolbar = () => {
    if (!showTerminal && !showInfo && !hasDocs && !hasAttachments) {
      return null;
    }

    return (
      <div id="header-toolbar">
        <div className="d-none d-sm-block">
          {renderRelated()}
          <ButtonGroup size="sm">
            {renderAttachments()}
            {showTerminal && renderTerminalButton()}
            {renderNotesButton()}
            {showInfo && renderInfoButton()}
            {hasDocs && renderDocsButton()}
            {isReport() && renderReportTrainingButton()}
          </ButtonGroup>
        </div>
        <div className="d-block d-sm-none">
          <ButtonToolbar>
            <DropdownButton
              variant="link"
              alignRight
              title={<i className="far fa-ellipsis-v h3" />}
              className="no-caret"
              id="mobile-header-toolbar"
            >
              {showInfo && (
                <Dropdown.Item onClick={toggleHelp}>
                  <span>{showHelp && <i className="far fa-check pr-1" />} Toggle Info</span>
                </Dropdown.Item>
              )}
              {hasDocs && (
                <Dropdown.Item onClick={openDocsModal} target="dsi-documentation">
                  Documentation
                </Dropdown.Item>
              )}
              {showTerminal && (
                <Dropdown.Item
                  onClick={() => {
                    TerminalService.open(pageName);
                  }}
                >
                  Launch Terminal
                </Dropdown.Item>
              )}
            </DropdownButton>
          </ButtonToolbar>
        </div>
      </div>
    );
  };

  const renderSubHeader = () => {
    if (!title) {
      return <span />;
    }

    const menuSection = menuDefs[whichMenuItem(pageName)];

    return (
      <div id="page-title-container">
        <div className="page-title-items">
          <div className="page-title-wrapper">
            {renderFavoriteIcon()} <h3 className="page-title">{title}</h3>
          </div>
          {children}
          {menuSection && (
            <MenuCrumb
              style={{ float: 'right' }}
              itemId={action === 'new' ? 'New' : itemId}
              pageName={pageName}
              sectionColor={menuSection.color}
              sectionLink={menuSection.link}
              sectionName={menuSection.label}
            />
          )}
          {renderToolbar()}
        </div>
      </div>
    );
  };

  return (
    <div id="header-container">
      <Helmet title={generateTitle()} />
      {showHeader && <div>{renderSubHeader()}</div>}
    </div>
  );
};

Header.propTypes = {
  children: PropTypes.object,
  hasDocs: PropTypes.bool,
  itemId: PropTypes.string,
  pageName: PropTypes.string,
  showFavorite: PropTypes.bool,
  showHeader: PropTypes.bool,
  showInfo: PropTypes.bool,
  showTerminal: PropTypes.bool,
  title: PropTypes.string,
};

export default Header;
